import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { HttpClient } from 'core/infra/http/HttpClient';
import { Scheduler } from 'managed-analytics/domain/Scheduler';
import { SchedulerRepository } from 'managed-analytics/infra/SchedulerRepository';
import { useSchedules } from 'managed-analytics/ui/hooks/useScheduler';
import { Dialog, Banner } from '@loadsmart/miranda-react';
import { 
  EmailSchedulerContainer,
  InfoBanner,
  InfoIcon,
  InfoText,
  TableContainer,
  StyledTable,
  TableHeader,
  StyledTableHeaderCell,  
  StyledCenterHeaderCell,
  TableBody,
  TableRow,
  StatusCell,
  ActionMenu,
  ActionButton,
  ActionDropdown,
  ActionItem,
  ActionCell,
  PaginationSection,
  ItemCount,
  PaginationControls,
  PaginationButton,
  PageInfo,
  StyledTableCell
} from './EmailScheduler.styles';
import { TruncatedText } from './EmailScheduler.styles';
import { EmptyState, EmptyStateDescription, EmptyStateImage, EmptyStateTitle, LockGlyph } from './EmailScheduler.styles';
import EmptyStateIllustration from 'assets/images/empty-state-activity.png';
import { usePBIExportableReports, usePBIReports } from 'managed-analytics/ui/hooks/usePBIReports';
import { TalkToSalesPendingDialog } from './TalkToSalesPendingDialog'
import SchedulerIconImg from 'assets/images/scheduler_icon.png';

interface EmailSchedulerProps {
  onScheduleNew?: (hasSchedules: boolean) => void;
  onEditSchedule?: (uuid: string) => void;
  successMessage?: string | null;
  onClearSuccessMessage?: () => void;
}

const OverlayBanner = styled.div`
  position: absolute;
  top: -52px;
  left: 25%;
  width: 50%;
  z-index: 10;
`;

const EmailScheduler: React.FC<EmailSchedulerProps> = ({
  onScheduleNew,
  successMessage,
  onClearSuccessMessage,
}) => {
  const { schedules, getSchedules, hasError } = useSchedules();
  // Calculate hasSchedules at the top
  const hasSchedules = schedules.length > 0;
  const { getExportableReports } = usePBIExportableReports();
  const { getReports, reports: fullReports } = usePBIReports();
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredData, setFilteredData] = useState<Scheduler[]>([]);
  const [paginatedData, setPaginatedData] = useState<Scheduler[]>([]);
  const [confirmDelete, setConfirmDelete] = useState<Scheduler | null>(null);
  const [isTalkToSalesPendingDialogOpen, setIsTalkToSalesPendingDialogOpen] = useState(false)
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(successMessage ? { type: 'success', message: successMessage } : null);
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);
  // Using native title attribute for hover (same as recurrency)
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  // Inline banner inside the card; no floating state required
  const paginationRef = useRef<HTMLDivElement>(null);
  const infoBannerRef = useRef<HTMLDivElement>(null);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
 
  const [pageSize, setPageSize] = useState(5);
  const totalPages = Math.ceil(filteredData.length / pageSize);

  const httpClient = new HttpClient(import.meta.env.VITE_BASE_API_URL);
  const schedulerRepo = new SchedulerRepository({ httpClient });

  useEffect(() => {
    getSchedules();
    getExportableReports();
    getReports();
    }, [getSchedules, getExportableReports]);
 
   // Dynamically calculate page size based on measured distance between tbody and pagination
   useEffect(() => {
    const calcPageSize = () => {
      const tbodyEl = tbodyRef.current as HTMLElement | null;
      const pagEl = paginationRef.current as HTMLElement | null;
      if (!tbodyEl || !pagEl) return;

      // Use viewport-based coordinates to avoid offsetParent differences
      const tbodyTop = tbodyEl.getBoundingClientRect().top;
      const pagTop = pagEl.getBoundingClientRect().top;
      const availablePx = Math.max(0, pagTop - tbodyTop);

      // Measure actual row height (no wrapping, fixed height styles applied)
            const firstRow = tbodyEl.querySelector('tr') as HTMLElement | null;
      const rowHeightMeasured = firstRow ? Math.ceil(firstRow.getBoundingClientRect().height) : 30;
      const rowHeight = rowHeightMeasured || 30;
 
      const rows = Math.max(1, Math.floor(availablePx / rowHeight));
 
      setPageSize(rows);
    };

    // Initial calc
    calcPageSize();

    // Observe size changes of wrapper, tbody, and pagination
    const ro = new ResizeObserver(() => calcPageSize());
    if (containerRef.current) ro.observe(containerRef.current);
    if (tbodyRef.current) ro.observe(tbodyRef.current);
    if (paginationRef.current) ro.observe(paginationRef.current);

    // Also recalc on window resize
    window.addEventListener('resize', calcPageSize);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', calcPageSize);
    };
    }, []);
 
  // Recalculate page size once data rows render on first load or when length changes
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const tbodyEl = tbodyRef.current as HTMLElement | null;
      const pagEl = paginationRef.current as HTMLElement | null;
      if (!tbodyEl || !pagEl) return;

      const tbodyTop = tbodyEl.getBoundingClientRect().top;
      const pagTop = pagEl.getBoundingClientRect().top;
      const availablePx = Math.max(0, pagTop - tbodyTop);

      const firstRow = tbodyEl.querySelector('tr') as HTMLElement | null;
      const rowHeightMeasured = firstRow ? Math.ceil(firstRow.getBoundingClientRect().height) : 30;
      const rowHeight = rowHeightMeasured || 30;

      const rows = Math.max(1, Math.floor(availablePx / rowHeight));
      setPageSize(rows);
    });
    return () => cancelAnimationFrame(id);
  }, [filteredData.length]);
 
   // Auto-hide banner after 10 seconds
  useEffect(() => {
    if (feedback?.type === 'success') {
      const id = setTimeout(() => setFeedback(null), 10000);
      return () => clearTimeout(id);
    }
  }, [feedback]);

  // Removed duplicate useSchedules hook and variable redeclaration
  useEffect(() => {
    if (successMessage) {
      const msg = (typeof successMessage === 'string' && successMessage.trim().length > 0)
        ? successMessage
        : 'Action completed successfully.';
      setFeedback({ type: 'success', message: msg });
      getSchedules();
      onClearSuccessMessage?.();
    }
  }, [successMessage, getSchedules]);

  // With search removed, mirror schedules into filteredData
  useEffect(() => {
    setFilteredData(schedules);
  }, [schedules]);

  useEffect(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    setPaginatedData(filteredData.slice(start, end));
  }, [currentPage, filteredData, pageSize]);

  // Ensure we don't shrink pageSize below the number of rows currently visible on the page.
  // This avoids creating an extra page before the current page is visually filled.
  useEffect(() => {
    if (pageSize < paginatedData.length) {
      setPageSize(paginatedData.length);
    }
  }, [pageSize, paginatedData.length]);

  // Clamp currentPage to totalPages after data or page size changes to avoid empty pages.
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [filteredData.length, pageSize, currentPage]);



  const handleDelete = async () => {
    if (!confirmDelete) return;

    // Remove any prior success banner while deleting
    if (feedback) setFeedback(null);

    try {
      await schedulerRepo.deleteSchedule(confirmDelete.uuid ?? '');
      setFeedback({ type: 'success', message: 'Schedule deleted successfully.' });
      getSchedules();
    } catch (err: any) {
      const msg = err?.response?.status === 404 ? 'Schedule not found or already deleted.' : 'Error deleting schedule.';
      setFeedback({ type: 'error', message: msg });
    } finally {
      setConfirmDelete(null);
    }
  };

  const toggleActionMenu = (uuid: string) => {
    setOpenActionMenu(openActionMenu === uuid ? null : uuid);
  };

  // No-op: native title tooltips used

  const formatRecurrence = (value: string) => {
    switch (value) {
      case 'weekdays':
        return 'Weekly';
      case 'monthdays':
        return 'Monthly';
      default:
        return value.charAt(0).toUpperCase() + value.slice(1);
    }
  };

  const formatRecurrenceDetail = (s: Scheduler): string => {
    const cfg: any = s.eventConfig || {};
    const days: string[] = cfg.days || [];
    const times: string[] = cfg.times || [];
    const tz: string = cfg.timezone || 'UTC';
    if (!days.length && !times.length) return '—';
    const daysLabel = days.length ? days.join(', ') : '';
    const timesLabel = times.length ? `at ${times.join(', ')}` : '';
    const space = daysLabel && timesLabel ? ' ' : '';
    return `${daysLabel}${space}${timesLabel} (${tz})`;
  };

  const getStatusIcon = (schedule: Scheduler) => {
  const report = schedule?.eventConfig?.report as any;
  const blockedFromSchedule = report?.isExecutionBlocked ?? report?.is_execution_blocked;
  const topLevelBlocked = (schedule as any)?.blocked;
  // If execution is blocked, always show lock
  const blocked = blockedFromSchedule ?? topLevelBlocked ?? false;
  return blocked ? 'lock' : 'check-circle';
  };

  const getStatusTooltip = (schedule: Scheduler) => {
    // Determine active by icon identifier (not the rendered character)
    const isActive = getStatusIcon(schedule) === 'check-circle';
    return isActive 
      ? 'Recurring delivery is active.\nThis report will be sent to your inbox as scheduled.'
      : 'A one-time preview was sent.\nRecurring delivery will be enabled once your account is upgraded.';
  };

  if (hasError) {
    return <div>Failed to load scheduled tasks.</div>;
  }

  const roleNameToProfile: Record<string, string> = useMemo(() => {
    const pairs = fullReports
      .filter(r => (r as any).roleName && (r as any).profile)
      .map(r => [(r as any).roleName as string, (r as any).profile as string] as [string, string]);
    return Object.fromEntries(pairs);
  }, [fullReports]);

  return (
    <EmailSchedulerContainer ref={containerRef}>
      {feedback && (
        <OverlayBanner>
          <Banner
            variant={feedback.type === 'success' ? 'success' : 'danger'}
            dismissible
            onDismiss={() => setFeedback(null)}
          >
            <Banner.Title>
              {feedback.message || (feedback.type === 'success' ? 'Action completed successfully.' : 'Something went wrong.')}
            </Banner.Title>
          </Banner>
        </OverlayBanner>
      )}

      {/* Green informational card */}
      <InfoBanner ref={infoBannerRef as any}>
        <InfoIcon>
          <img src={SchedulerIconImg} alt="Scheduler" style={{ width: 20, height: 20, display: 'block' }} />
        </InfoIcon>
        <InfoText>
          <div style={{ fontWeight: 600 }}>Get reports delivered automatically to your inbox—daily, weekly, or monthly.</div>
          <div style={{ marginTop: 4 }}>
            You'll receive a one-time preview when you schedule a report. Recurring delivery is a premium feature and will be enabled by our sales team.
          </div>
        </InfoText>
      </InfoBanner>

  {paginatedData.length > 0 ? (
        <>
          <TableContainer ref={tableRef as any}>
            <StyledTable>
              <TableHeader>
                <tr>
                  <StyledTableHeaderCell>Email subject</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Profile</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Report</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Recurrency</StyledTableHeaderCell>
                  <StyledCenterHeaderCell>Status</StyledCenterHeaderCell>
                  <StyledTableHeaderCell></StyledTableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody ref={tbodyRef as any}>
                {paginatedData.map((scheduler) => (
                  <TableRow key={scheduler.uuid}>
                      <StyledTableCell>{scheduler.name || scheduler.eventConfig?.report?.reportName || '—'}</StyledTableCell>
                      <StyledTableCell>{roleNameToProfile[scheduler.eventConfig?.report?.roleName as string] ?? '—'}</StyledTableCell>
                      <StyledTableCell>{scheduler.eventConfig?.report?.reportName ?? '—'}</StyledTableCell>
                      <StyledTableCell>
                        <TruncatedText title={`${formatRecurrence(scheduler.schedule)} - ${formatRecurrenceDetail(scheduler)}`}>
                          {`${formatRecurrence(scheduler.schedule)} - ${formatRecurrenceDetail(scheduler)}`}
                        </TruncatedText>
                      </StyledTableCell>
                    <StatusCell>
                      {getStatusIcon(scheduler) === 'check-circle' ? (
                        <span
                          title={getStatusTooltip(scheduler)}
                          style={{ fontSize: 16, lineHeight: '16px', color: '#0B7D6C', cursor: 'help' }}
                        >
                          ✓
                        </span>
                      ) : (
                        <span title={getStatusTooltip(scheduler)} style={{ display: 'inline-block', cursor: 'help' }}>
                          <LockGlyph />
                        </span>
                      )}
                    </StatusCell>
                    <ActionCell>
                      <ActionMenu>
                        <ActionButton onClick={() => toggleActionMenu(scheduler.uuid || '')}>
                          ⋮
                        </ActionButton>
                        <ActionDropdown $isOpen={openActionMenu === scheduler.uuid}>
                          <ActionItem
                            $isDelete
                            onClick={() => {
                              const report = scheduler?.eventConfig?.report as any;
                              const blockedFromSchedule = report?.isExecutionBlocked ?? report?.is_execution_blocked;
                              const topLevelBlocked = (scheduler as any)?.blocked;
                              const isBlocked = (blockedFromSchedule ?? topLevelBlocked ?? false) === true;
                              setOpenActionMenu(null);
                              if (isBlocked) {
                                setIsTalkToSalesPendingDialogOpen(true);
                                return;
                              }
                              setConfirmDelete(scheduler);
                            }}
                          >
                            Delete
                          </ActionItem>
                        </ActionDropdown>
                      </ActionMenu>
                    </ActionCell>
                  </TableRow>
                ))}
              </TableBody>
            </StyledTable>
          </TableContainer>
          <PaginationSection ref={paginationRef as any}>
            <ItemCount>{filteredData.length} items</ItemCount>
            <PaginationControls>
              <PaginationButton
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                &lt;&lt;
              </PaginationButton>
              <PaginationButton
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                &lt;
              </PaginationButton>
              <PageInfo>{currentPage} of {totalPages}</PageInfo>
              <PaginationButton
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                &gt;
              </PaginationButton>
              <PaginationButton
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                &gt;&gt;
              </PaginationButton>
            </PaginationControls>
          </PaginationSection>
        </>
      ) : (
        <TableContainer ref={tableRef as any}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '340px', height: '100%' }}>
            <EmptyState>
              <EmptyStateImage src={EmptyStateIllustration} alt="No schedules" />
              <EmptyStateTitle>Start with a one-time preview</EmptyStateTitle>
              <EmptyStateDescription>
                Schedule your first report email to see what automated delivery looks like.
              </EmptyStateDescription>
              <div style={{ marginTop: 8 }}>
                <button
                  onClick={() => onScheduleNew?.(hasSchedules)}
                  style={{
                    backgroundColor: '#005454',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    padding: '10px 14px',
                    cursor: 'pointer',
                  }}
                >
                  + Schedule first email
                </button>
              </div>
            </EmptyState>
          </div>
        </TableContainer>
      )}

      {/* Using native title tooltips; no custom tooltip rendering */}

      {/* Confirmation Dialog */}
      <Dialog
        open={!!confirmDelete}
        size="small"
        variant="danger"
        onClose={() => setConfirmDelete(null)}
      >
        <Dialog.Header>
          Confirm Deletion
          <Dialog.Close />
        </Dialog.Header>
        <Dialog.Body>
          Are you sure you want to delete the schedule{' '}
          <strong>{confirmDelete?.eventConfig?.report?.reportName}</strong>?
        </Dialog.Body>
        <Dialog.Actions>
          <Dialog.ActionSecondary onClick={() => setConfirmDelete(null)}>Cancel</Dialog.ActionSecondary>
          <Dialog.ActionPrimary onClick={handleDelete}>Delete</Dialog.ActionPrimary>
        </Dialog.Actions>
      </Dialog>
      <TalkToSalesPendingDialog
        isOpen={isTalkToSalesPendingDialogOpen}
        onClose={() => setIsTalkToSalesPendingDialogOpen(false)}
      />
      {/* Feedback handled via Banner above */}
    </EmailSchedulerContainer>
  );
};

export default EmailScheduler;